import { Helmet } from 'react-helmet-async';
import { getBaseUrl } from './sitemap.config';

const SITE_NAME = 'HirePanther';

export interface SeoProps {
  title?: string;
  description?: string;
  /** Path (e.g. /how-it-works). Canonical URL will be baseUrl + canonicalPath */
  canonicalPath?: string;
  /** Override full canonical URL (if set, canonicalPath is ignored) */
  canonicalUrl?: string;
  /** Open Graph image URL */
  ogImage?: string;
  /** Open Graph type */
  ogType?: string;
  /** Structured data (JSON-LD). Single object or array of objects. */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  /** Prevent search engines from indexing this page (for private/auth-gated pages) */
  noindex?: boolean;
}

export function Seo({
  title,
  description,
  canonicalPath,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  structuredData,
  noindex = false,
}: SeoProps) {
  const baseUrl = getBaseUrl();
  const canonical =
    canonicalUrl ||
    (canonicalPath
      ? `${baseUrl.replace(/\/$/, '')}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`
      : undefined);
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const ogImageUrl = ogImage?.startsWith('http')
    ? ogImage
    : ogImage
      ? `${baseUrl}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`
      : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}

      {/* JSON-LD */}
      {structuredData != null && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(structuredData) ? structuredData : [structuredData]
          )}
        </script>
      )}
    </Helmet>
  );
}
